<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">Форма сделки</div>
    <v-container>
      <div class="form-base">
        <v-radio-group v-model="acceptanceEscrowDetails.dealForm">
          <v-radio
            :label="dealFormToLabel[DealForm.SIMPLE]"
            :value="DealForm.SIMPLE"
            disabled
          />
          <v-radio
            :label="dealFormToLabel[DealForm.NOTARIAL]"
            :value="DealForm.NOTARIAL"
          />
        </v-radio-group>

        <v-row>
          <v-col cols="12">
            <div class="form-base__title">
              Сведения о сделке, для исполнения которой принимаются на
              депонирование безналичные денежные средства
            </div>
          </v-col>

          <v-col
            cols="12"
            v-if="acceptanceEscrowDetails.dealForm === DealForm.SIMPLE"
          >
            <v-text-field
              v-model="
                $v.acceptanceEscrowDetails[DealForm.SIMPLE].dealInfoRegNum
                  .$model
              "
              label="Номер"
              :error-messages="
                getValidationErrorText(
                  $v.acceptanceEscrowDetails[DealForm.SIMPLE].dealInfoRegNum
                )
              "
              outlined
            />
          </v-col>

          <template
            v-if="acceptanceEscrowDetails.dealForm === DealForm.NOTARIAL"
          >
            <v-col cols="12">
              <v-text-field
                v-model="
                  $v.acceptanceEscrowDetails[DealForm.NOTARIAL].dealInfoRegNum
                    .$model
                "
                :error-messages="
                  getValidationErrorText(
                    $v.acceptanceEscrowDetails[DealForm.NOTARIAL].dealInfoRegNum
                  )
                "
                label="Регистрационный номер в реестре нотариальных действий"
                outlined
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="
                  $v.acceptanceEscrowDetails[DealForm.NOTARIAL]
                    .dealInfoNotaryInfo.$model
                "
                :error-messages="
                  getValidationErrorText(
                    $v.acceptanceEscrowDetails[DealForm.NOTARIAL]
                      .dealInfoNotaryInfo
                  )
                "
                label="Cведения о нотариусе, удостоверившем сделку"
                outlined
              />
            </v-col>
          </template>

          <v-col cols="6">
            <DateFieldCustom
              label="Дата совершения"
              :value="$v.acceptanceEscrowDetails.dealInfoDate.$model"
              :errorMessage="$v.acceptanceEscrowDetails.dealInfoDate"
              :max="new Date().toISOString()"
              @change="$v.acceptanceEscrowDetails.dealInfoDate.$model = $event"
            />
          </v-col>
          <v-col cols="6">
            <CurrencyField
              v-model="$v.acceptanceEscrowDetails.depositAmount.$model"
              label="Общий размер вносимых денежных средств"
              :errorMessages="
                getValidationErrorText($v.acceptanceEscrowDetails.depositAmount)
              "
            />
          </v-col>
        </v-row>
      </div>

      <Footer
        @prev-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.PREV,
              step: StepName.S3,
            })
        "
        @next-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.NEXT,
              step: StepName.S3,
            })
        "
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { required, requiredIf, and, maxLength } from 'vuelidate/lib/validators';

import DateFieldCustom from '@/components/base/DateFieldCustom';
import CurrencyField from '@/components/base/CurrencyField.vue';
import {
  CURRENCY_MAX_LENGTH,
  CURRENCY_ONLY_POSITIVE,
  DATE_FORMAT,
  DATE_FUTURE,
  DATE_PAST,
  EMOJI,
  getValidationErrorText,
} from '@/utils/validate';
import Footer from '@/components/services/elements/Footer';
import {
  checkInvalid,
  DealForm,
  dealFormToLabel,
  leaveStep,
} from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import { StepDirection, StepName } from '@/types/services';

export default {
  name: 'Step3',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Footer,
    DateFieldCustom,
    CurrencyField,
  },
  data: () => ({
    StepDirection,
    StepName,
    leaveStep,
    DealForm,
    dealFormToLabel,
    getValidationErrorText,
    invalidFields: [],
    acceptanceEscrowDetails: {
      dealForm: DealForm.NOTARIAL,
      dealInfoDate: '',
      depositAmount: null,
      [DealForm.SIMPLE]: {
        dealInfoRegNum: '',
      },
      [DealForm.NOTARIAL]: {
        dealInfoRegNum: '',
        dealInfoNotaryInfo: '',
      },
    },
  }),
  validations() {
    return {
      acceptanceEscrowDetails: {
        dealInfoDate: {
          required,
          DATE: and(DATE_FORMAT, DATE_FUTURE, DATE_PAST),
        },
        depositAmount: {
          required,
          CURRENCY_MAX_LENGTH,
          CURRENCY_ONLY_POSITIVE,
        },
        [DealForm.SIMPLE]: {
          dealInfoRegNum: {
            required: requiredIf(
              () => this.acceptanceEscrowDetails.dealForm === DealForm.SIMPLE
            ),
            maxLength: maxLength(50),
            EMOJI,
          },
        },
        [DealForm.NOTARIAL]: {
          dealInfoRegNum: {
            required: requiredIf(
              () => this.acceptanceEscrowDetails.dealForm === DealForm.NOTARIAL
            ),
            maxLength: maxLength(50),
            EMOJI,
          },
          dealInfoNotaryInfo: {
            required: requiredIf(
              () => this.acceptanceEscrowDetails.dealForm === DealForm.NOTARIAL
            ),
            maxLength: maxLength(4000),
            EMOJI,
          },
        },
      },
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      if (!ServicesModule.statement.acceptanceEscrowDetails) {
        return;
      }

      const statementStore = cloneDeep(
        ServicesModule.statement.acceptanceEscrowDetails
      );

      this.acceptanceEscrowDetails = {
        ...this.acceptanceEscrowDetails,
        ...statementStore,
      };

      if (statementStore.dealForm === DealForm.SIMPLE) {
        this.acceptanceEscrowDetails[DealForm.SIMPLE].dealInfoRegNum =
          statementStore.dealInfoRegNum;
      }

      if (statementStore.dealForm === DealForm.NOTARIAL) {
        this.acceptanceEscrowDetails[DealForm.NOTARIAL].dealInfoRegNum =
          statementStore.dealInfoRegNum;
        this.acceptanceEscrowDetails[DealForm.NOTARIAL].dealInfoNotaryInfo =
          statementStore.dealInfoNotaryInfo;
      }
    },

    assemblyModel() {
      let model = cloneDeep(this.acceptanceEscrowDetails);

      delete model[DealForm.SIMPLE];
      delete model[DealForm.NOTARIAL];

      if (this.acceptanceEscrowDetails.dealForm === DealForm.SIMPLE) {
        model = deepmerge(model, this.acceptanceEscrowDetails[DealForm.SIMPLE]);
      }

      if (this.acceptanceEscrowDetails.dealForm === DealForm.NOTARIAL) {
        model = deepmerge(
          model,
          this.acceptanceEscrowDetails[DealForm.NOTARIAL]
        );
      }

      return model;
    },

    touchFields() {
      this.invalidFields = [];

      this.$v.$touch();
      checkInvalid(this.$v, this.invalidFields);
    },
  },
};
</script>
